import React, {createContext, useEffect, useMemo, useState} from 'react';

import {envIsPublicCloud} from '../../config';
import {
  FailedPaymentBanners,
  OrganizationDataForFailedPaymentBanner,
  OrganizationSubscriptionsQuery,
  useFailedPaymentBannersQuery,
  useOrganizationSubscriptionsQuery,
} from '../../generated/graphql';
import {useViewerIsWandbEmployee} from '../../pages/OrgDashboard/useIsOrgDashboardEnabled';
import {useViewer} from '../../state/viewer/hooks';
import {Viewer} from '../../state/viewer/types';
import {
  isNonAcademicOrg,
  Org,
  PlanInfoWithActualPlans,
  useSubscriptionPlans,
} from '../../util/pricing';
import {
  getLegacyNudgeBarTypeForViewerOrg,
  getStorageBannerFlags,
  getTeamsPlanBannerFlags,
  getViewerOrSimulatedOrg,
  LegacyNudgeBarType,
  orgFromViewerOrg,
  StorageBannerFlags,
  TeamsPlanBannerFlags,
} from '.';
import {
  FreeAccountEnforcementBannerFlags,
  getFreeAccountEnforcementBannerFlags,
} from './FreeAccountEnforcementBanners';

export type GlobalBannerState = {
  // legacy stuff
  organization: Org | null;
  renderedNudgeBar: LegacyNudgeBarType | null;
  maxHours: number | null;
  aggressive?: boolean;
  daysUntilEnforcement?: number;

  // new way of retrieving banner information
  storageAndEnforcementBannerFlags: StorageBannerFlags;
  teamsPlanEnforcementBannerFlags: TeamsPlanBannerFlags;
  failedPaymentOrgData: OrganizationDataForFailedPaymentBanner | null;
  freeAccountEnforcementBannerFlags: FreeAccountEnforcementBannerFlags;
};

type GlobalBannerStates = {
  states: GlobalBannerState[] | null;
};

export const GlobalBannerContext = createContext<GlobalBannerStates>({
  states: [],
});

type GlobalBannerUpdaterState = {
  refetch: () => Promise<any>;
};
export const GlobalBannerUpdaterContext =
  createContext<GlobalBannerUpdaterState>({
    refetch: () => Promise.resolve(),
  });

export const GlobalBannerContextProvider = React.memo(({children}) => {
  const {data, refetch} = useOrganizationSubscriptionsQuery();
  const {planInfo} = useSubscriptionPlans();

  const viewerOrgs = useMemo(() => data?.viewer?.organizations ?? [], [data]);

  const isViewerWandbEmployee = useViewerIsWandbEmployee();

  const [contextValue, setContextValue] = useState<GlobalBannerStates>({
    states: [],
  });

  const viewer = useViewer();

  const paymentBannerQuery = useFailedPaymentBannersQuery({
    variables: {id: viewer?.id as string},
    skip: viewer?.id == null || !envIsPublicCloud,
  });

  const failedPaymentBanners = paymentBannerQuery.loading
    ? undefined
    : (paymentBannerQuery.data?.failedPaymentBanners as FailedPaymentBanners);

  useEffect(() => {
    const fetchGlobalBannerState = async () => {
      const states = await getGlobalBannerState(
        viewerOrgs,
        planInfo,
        viewer,
        failedPaymentBanners,
        isViewerWandbEmployee
      );
      setContextValue(states);
    };
    fetchGlobalBannerState();
  }, [
    viewerOrgs,
    planInfo,
    viewer,
    failedPaymentBanners,
    isViewerWandbEmployee,
  ]);

  const updaterContextValue = useMemo(() => ({refetch}), [refetch]);

  return (
    <GlobalBannerContext.Provider value={contextValue}>
      <GlobalBannerUpdaterContext.Provider value={updaterContextValue}>
        {children}
      </GlobalBannerUpdaterContext.Provider>
    </GlobalBannerContext.Provider>
  );
});

export default GlobalBannerContextProvider;

export type ViewerOrgs = NonNullable<
  OrganizationSubscriptionsQuery['viewer']
>['organizations'];
export type ViewerOrg = ViewerOrgs[number];

async function getGlobalBannerState(
  viewerOrgs: ViewerOrgs,
  plans: PlanInfoWithActualPlans | null,
  viewer?: Viewer,
  failedPaymentBanners?: FailedPaymentBanners,
  isViewerWandbEmployee?: boolean
): Promise<GlobalBannerStates> {
  const states: GlobalBannerState[] = [];

  for (const viewerOrg of viewerOrgs) {
    // exclude academic orgs from all enforcement banners
    if (!isNonAcademicOrg(viewerOrg)) {
      continue;
    }

    const viewerOrSimulatedOrg = getViewerOrSimulatedOrg(viewerOrg, plans);
    const legacyNudgeBarsForOrg =
      getLegacyNudgeBarTypeForViewerOrg(viewerOrSimulatedOrg);

    let teamsPlanEnforcementBannerFlags = {};
    let storageBannerFlags = {};
    let freeAccountEnforcementBannerFlags = {};

    if (!isViewerWandbEmployee) {
      try {
        [
          teamsPlanEnforcementBannerFlags,
          storageBannerFlags,
          freeAccountEnforcementBannerFlags,
        ] = await Promise.all([
          getTeamsPlanBannerFlags(viewerOrg),
          getStorageBannerFlags(viewer, viewerOrSimulatedOrg),
          getFreeAccountEnforcementBannerFlags(viewer, viewerOrg),
        ]);
      } catch (error) {
        console.error(
          `Error getting banner flags for org ${viewerOrg.name}: ${error}`
        );
      }
    }

    let orgFailedPaymentBanner: OrganizationDataForFailedPaymentBanner | null =
      null;
    if (failedPaymentBanners) {
      orgFailedPaymentBanner =
        failedPaymentBanners.orgsWithFailedPayments.find(
          b => b?.orgName === viewerOrg.name
        ) ?? null;
    }

    if (legacyNudgeBarsForOrg != null) {
      const organization = orgFromViewerOrg(viewerOrSimulatedOrg);
      const renderedNudgeBar = legacyNudgeBarsForOrg.type;
      const maxHours = legacyNudgeBarsForOrg.maxComputeHours ?? null;
      const aggressive = legacyNudgeBarsForOrg.aggressive;
      const daysUntilEnforcement = legacyNudgeBarsForOrg.daysUntilEnforcement;
      states.push({
        organization,
        renderedNudgeBar,
        maxHours,
        aggressive,
        daysUntilEnforcement,
        storageAndEnforcementBannerFlags: storageBannerFlags,
        teamsPlanEnforcementBannerFlags,
        failedPaymentOrgData: orgFailedPaymentBanner,
        freeAccountEnforcementBannerFlags,
      });
    } else {
      states.push({
        organization: orgFromViewerOrg(viewerOrSimulatedOrg),
        renderedNudgeBar: null,
        maxHours: null,
        storageAndEnforcementBannerFlags: storageBannerFlags,
        teamsPlanEnforcementBannerFlags,
        failedPaymentOrgData: orgFailedPaymentBanner,
        freeAccountEnforcementBannerFlags,
      });
    }
  }

  return {states};
}
